// Test notification utilities for debugging FCM
import { getFunctions, httpsCallable } from 'firebase/functions';
import { createLogger } from './logger';

const logger = createLogger('TestNotifications');

/**
 * Test function to send a notification to the current user
 */
export const testSendNotification = async (currentUser) => {
  if (!currentUser) {
    logger.warn('No current user available for testing');
    return;
  }

  try {
    logger.info('Testing notification system...');
    
    const functions = getFunctions();
    const sendOrderStatusNotification = httpsCallable(functions, 'sendOrderStatusNotification');
    
    const testData = {
      userId: currentUser.uid,
      orderId: 'test-order-' + Date.now(),
      status: 'test',
      message: 'This is a test notification from PackMyCart'
    };
    
    logger.info('Sending test notification with data:', testData);
    
    const result = await sendOrderStatusNotification(testData);
    logger.info('Test notification result:', result.data);
    
    return result.data;
  } catch (error) {
    logger.error('Test notification failed:', error);
    throw error;
  }
};

/**
 * Check if FCM is properly configured
 */
export const checkFCMConfiguration = () => {
  // In React, environment variables are available at build time
  // We need to check if they were properly injected during build
  const config = {
    vapidKey: process.env.REACT_APP_FCM_VAPID_KEY,
    employeeNotifications: process.env.REACT_APP_ENABLE_EMPLOYEE_NOTIFICATIONS,
    customerNotifications: process.env.REACT_APP_ENABLE_CUSTOMER_NOTIFICATIONS,
    cartNotificationDuration: process.env.REACT_APP_CART_NOTIFICATION_DURATION
  };
  
  logger.info('FCM Configuration Check:', config);
  
  const issues = [];
  const warnings = [];
  
  // Check if environment variables are undefined (not set during build)
  if (typeof config.vapidKey === 'undefined') {
    issues.push('FCM VAPID key not available (environment variable not set)');
  } else if (!config.vapidKey || config.vapidKey === 'your-vapid-key-here') {
    issues.push('FCM VAPID key not configured properly');
  }
  
  // Check notification flags - they might be undefined if not set in GitHub Variables
  // This is not critical since the Cloud Functions have their own configuration
  if (typeof config.employeeNotifications === 'undefined') {
    warnings.push('Employee notifications environment variable not set (Cloud Functions will use their own config)');
  } else if (config.employeeNotifications !== 'true') {
    warnings.push('Employee notifications disabled via environment variable');
  }
  
  if (typeof config.customerNotifications === 'undefined') {
    warnings.push('Customer notifications environment variable not set (Cloud Functions will use their own config)');
  } else if (config.customerNotifications !== 'true') {
    warnings.push('Customer notifications disabled via environment variable');
  }
  
  // Only show critical issues, not warnings
  if (issues.length > 0) {
    logger.warn('FCM Configuration Issues:', issues);
    return { status: 'issues', issues, warnings, config };
  } else {
    logger.info('FCM Configuration looks good');
    return { status: 'ok', issues: [], warnings, config };
  }
};
